import { Component, ElementRef, Input, Output, EventEmitter, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Self-styled on/off toggle. Replaces `mat-slide-toggle`.
 *
 * Supports both usage modes:
 *  - Template-driven: `[checked]` input + `(change)` output (emits a boolean).
 *  - Reactive forms:  `formControlName` (implements ControlValueAccessor).
 *
 * Label text can be projected: `<app-form-toggle>My label</app-form-toggle>`.
 */
@Component({
    selector: 'app-form-toggle',
    templateUrl: './toggle.component.html',
    styleUrls: ['./toggle.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormToggleComponent),
            multi: true,
        },
    ],
})
export class FormToggleComponent implements ControlValueAccessor {
    @ViewChild('switchElement') switchElement?: ElementRef<HTMLElement>;

    private static nextId = 0;

    readonly labelId = `form-toggle-label-${FormToggleComponent.nextId++}`;

    @Input() ariaLabel: string | null = null;

    @Input() ariaDescribedby: string | null = null;

    @Input()
    get checked(): boolean {
        return this._checked;
    }

    set checked(value: boolean) {
        this._checked = !!value;
    }

    @Input() disabled = false;

    @Output('change') changeOutput = new EventEmitter<boolean>();

    _checked = false;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onChange: (value: boolean) => void = () => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onTouched: () => void = () => {};

    onContainerClick(): void {
        if (this.disabled) return;
        this.switchElement?.nativeElement.focus();
        this.toggle();
    }

    toggle(): void {
        if (this.disabled) return;
        this._checked = !this._checked;
        this.onChange(this._checked);
        this.onTouched();
        this.changeOutput.emit(this._checked);
    }

    // ControlValueAccessor
    writeValue(value: boolean): void {
        this._checked = !!value;
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
